///
/// EarthBrowser
///
/// Created by Matt Giger on 10/25/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The sequence as a batched cloud of spheres in the block frame, plus the
/// same geometry flattened onto the two far walls as shadow discs: a 3D
/// scatter with its marginal projections, which is what makes depth
/// legible without rotating the camera. Appearance follows the scene clock
/// in the shader (see cutout_hypocenter.glsl).
///

import { boxDepthKm, hypocenterRadiusKm, toBlockXZ, type CutoutLayout, type SequenceEvent } from "./cutoutLayout"
import { type FarWalls } from "./CutoutWallsNode"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type cube } from "@stovokor/math/cube"
import { type matrix } from "@stovokor/math/matrix"
import { type sphere } from "@stovokor/math/sphere"
import { vec3 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

const cLatSegments = 5
const cLonSegments = 8
/** Shadow discs sit a hair inside the wall plane */
const cShadowInsetFraction = 0.002

export class HypocenterCloudNode extends NodeBase {
	private readonly geometry: HypocenterCloudGeometry
	private readonly solid: HypocenterRenderer
	private readonly shadowX: HypocenterRenderer
	private readonly shadowZ: HypocenterRenderer
	private readonly halfX: number
	private readonly halfZ: number
	private readonly mainshockTimeMs: number

	constructor(context: StovokorContext, layout: CutoutLayout, events: SequenceEvent[], mainshockId: string, mainshockTimeMs: number) {
		super()
		this.mainshockTimeMs = mainshockTimeMs
		this.halfX = layout.lengthKm / 2
		this.halfZ = layout.widthKm / 2
		this.geometry = new HypocenterCloudGeometry(context, layout, events, mainshockId, mainshockTimeMs)

		this.solid = new HypocenterRenderer(context, this.geometry, false)
		this.addNode(new RendererNode(this.solid, 0))

		this.shadowX = new HypocenterRenderer(context, this.geometry, true)
		this.shadowX.flattenMask.value = new vec3(1, 0, 0)
		this.addNode(new RendererNode(this.shadowX, 1))

		this.shadowZ = new HypocenterRenderer(context, this.geometry, true)
		this.shadowZ.flattenMask.value = new vec3(0, 0, 1)
		this.addNode(new RendererNode(this.shadowZ, 1))

		this.setFarWalls({ signX: 1, signZ: 1 })
	}

	public get eventCount(): number {
		return this.geometry.eventCount
	}

	/** Scene clock in ms; the shader hides events that haven't happened yet */
	public setClock(clockMs: number): void {
		const t = (clockMs - this.mainshockTimeMs) / 1000
		this.solid.clockT.value = t
		this.shadowX.clockT.value = t
		this.shadowZ.clockT.value = t
	}

	public set reveal(value: number) {
		this.solid.reveal.value = value
		this.shadowX.reveal.value = value
		this.shadowZ.reveal.value = value
	}

	public set opacity(value: number) {
		this.solid.opacity.value = value
		this.shadowX.opacity.value = value
		this.shadowZ.opacity.value = value
	}

	public setFarWalls(far: FarWalls): void {
		const inset = cShadowInsetFraction * Math.max(this.halfX, this.halfZ) * 2
		this.shadowX.planeCoord.value = -far.signX * (this.halfX - inset)
		this.shadowZ.planeCoord.value = -far.signZ * (this.halfZ - inset)
	}

	override destroy(): void {
		super.destroy()
		this.geometry.release()
	}
}

/**
 * Historical catalog events inside the footprint as pale translucent
 * "ghosts": the same batched sphere mesh in ghost mode, no wall shadows.
 * They never pop (decades old) and never gate on the clock in practice
 */
export class GhostCloudNode extends NodeBase {
	private readonly geometry: HypocenterCloudGeometry
	private readonly ghostRenderer: HypocenterRenderer

	constructor(context: StovokorContext, layout: CutoutLayout, events: SequenceEvent[], mainshockTimeMs: number) {
		super()
		this.geometry = new HypocenterCloudGeometry(context, layout, events, "", mainshockTimeMs, cKindGhost)
		this.ghostRenderer = new HypocenterRenderer(context, this.geometry, false, true)
		// Historical events are at most a few decades back; the clock never
		// reaches them, so pin the shader's clock far in the future
		this.ghostRenderer.clockT.value = 1e9
		this.addNode(new RendererNode(this.ghostRenderer, 1))
	}

	public get eventCount(): number {
		return this.geometry.eventCount
	}

	public set reveal(value: number) {
		this.ghostRenderer.reveal.value = value
	}

	public set opacity(value: number) {
		this.ghostRenderer.opacity.value = value
	}

	override destroy(): void {
		super.destroy()
		this.geometry.release()
	}
}

const cKindGhost = 3

/** Leaf node wrapping one renderer; shadows draw after the walls (order 1) */
class RendererNode extends NodeBase {
	constructor(renderer: Renderer, order: number) {
		super()
		this.renderer = renderer
		this.renderOrder = order
	}
}

class HypocenterRenderer extends Renderer {
	public modelViewProjection = new UniformDynamic<matrix>()
	public modelView = new UniformDynamic<matrix>()
	public clockT = new UniformDynamic<number>(0)
	public reveal = new UniformDynamic<number>(0)
	public time = new UniformDynamic<number>(0)
	public shadowMode = new UniformDynamic<number>(0)
	public flattenMask = new UniformDynamic<vec3>(new vec3(1, 0, 0))
	public planeCoord = new UniformDynamic<number>(0)
	public opacity = new UniformDynamic<number>(1)

	constructor(context: StovokorContext, geometry: Geometry, shadow: boolean, ghost: boolean = false) {
		const materials: Material[] = shadow || ghost ? [{ transparency: true, available: true }] : []
		super(context, new ShaderSourceDefinition("cutout_hypocenter"), geometry, materials)
		this.shadowMode.value = shadow ? 1 : 0
		// Solid spheres: both sides so winding never matters. Shadows fold
		// both hemispheres onto one disc and ghosts are translucent, so both
		// cull back faces to keep the alpha from doubling
		this.drawState.cullFace = shadow || ghost
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
		this.modelView.value = state.modelView
		this.time.value = (state.realTime.julian % 1) * 86400
	}
}

/**
 * All spheres in one vertex buffer. Shared by the solid and the two shadow
 * renderers; the renderers' destroy() calls are no-ops and the owning node
 * releases the buffers once
 */
class HypocenterCloudGeometry implements Geometry {
	public center: VertexAttribute
	public unit: VertexAttribute
	public radius: VertexAttribute
	public eventTime: VertexAttribute
	public depthT: VertexAttribute
	public kind: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public bounds: cube
	public boundingSphere: sphere
	public available: boolean = true
	public readonly eventCount: number
	private released = false

	/**
	 * @param kindOverride - when set, every event gets this kind (ghost
	 * clouds) instead of mainshock / sequence / background classification
	 */
	constructor(context: StovokorContext, layout: CutoutLayout, events: SequenceEvent[], mainshockId: string, mainshockTimeMs: number, kindOverride?: number) {
		this.center = new VertexAttribute(context, 3)
		this.unit = new VertexAttribute(context, 3)
		this.radius = new VertexAttribute(context, 1)
		this.eventTime = new VertexAttribute(context, 1)
		this.depthT = new VertexAttribute(context, 1)
		this.kind = new VertexAttribute(context, 1)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.center, this.unit, this.radius, this.eventTime, this.depthT, this.kind])

		// Unit sphere template
		const template: vec3[] = []
		for(let lat = 0; lat <= cLatSegments; lat++) {
			const phi = (lat / cLatSegments) * Math.PI - Math.PI / 2
			const y = Math.sin(phi)
			const ring = Math.cos(phi)
			for(let lon = 0; lon <= cLonSegments; lon++) {
				const theta = (lon / cLonSegments) * Math.PI * 2
				template.push(new vec3(Math.cos(theta) * ring, y, Math.sin(theta) * ring))
			}
		}
		const stride = cLonSegments + 1
		const boxDepth = boxDepthKm(layout)

		let count = 0
		for(const e of events) {
			const xz = toBlockXZ(e.east - layout.centerEast, e.north - layout.centerNorth, layout.strike)
			// Only what falls inside the footprint; the block is sized to the sequence
			if(Math.abs(xz.x) > layout.lengthKm / 2 || Math.abs(xz.z) > layout.widthKm / 2) continue
			const px = xz.x
			const py = -Math.min(e.depthKm, layout.depthFloorKm) * layout.depthScale
			const pz = xz.z
			const isMain = kindOverride === undefined && e.id === mainshockId
			const kind = kindOverride ?? (isMain ? 2 : e.inSequence ? 1 : 0)
			const sizeFactor = isMain ? 1.25 : kind === 3 ? 0.8 : e.inSequence ? 1 : 0.7
			const r = hypocenterRadiusKm(layout, e.mag) * sizeFactor
			const t = (e.timeMs - mainshockTimeMs) / 1000
			// Depth color always from the true depth, wherever the sphere is drawn
			const depthT = Math.min(1, e.depthKm / layout.depthFloorKm)

			const base = this.center.count
			for(const u of template) {
				this.center.add(px, py, pz)
				this.unit.add(u.x, u.y, u.z)
				this.radius.add(r)
				this.eventTime.add(t)
				this.depthT.add(depthT)
				this.kind.add(kind)
				this.center.bounds.update(px + u.x * r, py + u.y * r, pz + u.z * r)
			}
			for(let lat = 0; lat < cLatSegments; lat++) {
				for(let lon = 0; lon < cLonSegments; lon++) {
					const a = base + lat * stride + lon
					const b = a + stride
					this.indexBuffer.quad(a, b, b + 1, a + 1)
				}
			}
			count++
		}
		this.eventCount = count

		// Shadows reach the wall planes, so cull against the whole box
		this.center.bounds.update(-layout.lengthKm / 2, -boxDepth, -layout.widthKm / 2)
		this.center.bounds.update(layout.lengthKm / 2, 0, layout.widthKm / 2)
		this.bounds = this.center.bounds
		this.boundingSphere = this.bounds.sphere
	}

	/** Renderer-driven destroy is a no-op; see release() */
	public destroy(): void {}

	public release(): void {
		if(this.released) return
		this.released = true
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}
